import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserCheck, AlertCircle, Loader } from 'lucide-react';
// import { useAuth } from '../contexts/AuthContext';

const StudentLogin: React.FC = () => {
  const [dni, setDni] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState('es');
  const navigate = useNavigate();

  const texts = {
    es: {
      title: 'CHILDREN IBC',
      subtitle: '🌟 Test de Ubicación de Inglés 🌟',
      welcome: '¡Bienvenido/a! 👋',
      description: 'Para iniciar tu Test de Ubicación, ingresa tu número de DNI a continuación. 🎓✨',
      dniLabel: '📝 Número de DNI',
      dniPlaceholder: 'Ej: 12345678',
      button: '🚀 Iniciar Test de Ubicación 🚀',
      buttonLoading: 'Verificando... 🔍',
      adminLink: '👨‍💼 ¿Eres administrador? Ingresa aquí 👨‍💼',
      note: '📢 Nota: Si no puedes acceder, contacta al área académica para que autorice tu DNI. ',
      whatsappLink: '📞'
    },
    en: {
      title: 'CHILDREN IBC',
      subtitle: '🌟 English Placement Test 🌟',
      welcome: 'Welcome! 👋',
      description: 'To start your Placement Test, enter your ID number below. 🎓✨',
      dniLabel: '📝 ID Number',
      dniPlaceholder: 'Ex: 12345678',
      button: '🚀 Start Placement Test 🚀',
      buttonLoading: 'Verifying... 🔍',
      adminLink: '👨‍💼 Are you an administrator? Login here 👨‍💼',
      note: '📢 Note: If you cannot access, contact the academic area to authorize your ID. ',
      whatsappLink: '📞'
    }
  };

  const currentTexts = texts[language as keyof typeof texts];

  const handleWhatsAppClick = () => {
    const phoneNumber = '987402662';
    const message = language === 'es' 
      ? 'Hola, necesito autorización para acceder al Test de Ubicación de IBC. Mi DNI es:'
      : 'Hello, I need authorization to access the IBC Placement Test. My ID is:';
    const whatsappUrl = `https://wa.me/51${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!dni.trim()) {
      setError('Por favor ingresa tu DNI');
      return;
    }

    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/auth/student/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dni: dni.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.hasCompletedTest) {
          // Ya completó el examen, mostrar error con información del resultado
          setError(`Ya realizaste el test de ubicación y obtuviste: ${data.student.nivelObtenido || 'Nivel obtenido'}`);
        } else if (data.token) {
          // Guardar token y datos del usuario solo si hay token
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          // Forzar recarga para actualizar el estado de autenticación
          window.location.href = '/placement/test';
        } else {
          setError('Error: No se recibió token de autenticación');
        }
      } else {
        setError(data.message || data.error || 'Error al iniciar sesión');
      }
    } catch (error) {
      console.error('Error en login:', error);
      setError('Error de conexión con el servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-login-container">
      {/* Selector de idioma */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        className="language-selector"
      >
        <button
          onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
          className={`language-btn ${language === 'es' ? 'active' : ''}`}
        >
          {language === 'es' ? '🇪🇸 ES' : '🇺🇸 EN'}
        </button>
      </motion.div>

      <div className="login-layout">
        {/* Mitad izquierda - Logo y contenido educativo */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="logo-section"
        >
          <div className="logo-container">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
              className="logo-wrapper"
            >
              <img src="/logo.png" alt="IBC Logo" className="ibc-logo" />
            </motion.div>
            
            {/* Contenido educativo oculto */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="educational-content"
            >
              <div className="welcome-card">
                <h2 className="welcome-title">¡Bienvenido!</h2>
                <p className="welcome-text">International Bilingual Certification Institute</p>
              </div>
              
              <div className="motivation-card">
                <h3 className="motivation-title">¡Aprendamos y Crezcamos!</h3>
                <p className="motivation-text">¡Tu aventura comienza aquí!</p>
              </div>
            </motion.div> */}
          </div>
        </motion.div>

        {/* Mitad derecha - Formulario de login */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="form-section"
        >
          <div className="login-form-card">
            {/* Header del formulario */}
            <div className="form-header">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
              >
                <h1 className="form-title">{currentTexts.title}</h1>
                <p className="form-subtitle">{currentTexts.subtitle}</p>
              </motion.div>
            </div>

            {/* Instrucciones */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="form-instructions"
            >
              <h2 className="form-welcome">
                <UserCheck className="mr-2" />
                {currentTexts.welcome}
              </h2>
              <p className="form-description">
                {currentTexts.description}
              </p>
            </motion.div>

            {/* Formulario */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              onSubmit={handleSubmit} 
              className="form-content"
            >
              <div className="input-group">
                <label htmlFor="dni" className="input-label">
                  {currentTexts.dniLabel}
                </label>
                <input
                  type="text"
                  id="dni"
                  value={dni}
                  onChange={(e) => setDni(e.target.value.replace(/\D/g, ''))}
                  placeholder={currentTexts.dniPlaceholder}
                  maxLength={8}
                  className="form-input"
                  disabled={loading}
                />
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="error-message"
                >
                  <AlertCircle className="mr-3" size={20} />
                  <p>{error}</p>
                </motion.div>
              )}

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                disabled={loading || !dni.trim()}
                className="form-button"
              >
                {loading ? (
                  <>
                    <Loader className="mr-2" size={24} />
                    {currentTexts.buttonLoading}
                  </>
                ) : (
                  currentTexts.button
                )}
              </motion.button>
            </motion.form>

            {/* Links adicionales */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="form-footer"
            >
              <button
                onClick={() => navigate('/admin/login')}
                className="admin-link"
              >
                {currentTexts.adminLink}
              </button>
              
              <div className="info-note">
                <p>
                  {currentTexts.note}
                  <button 
                    onClick={handleWhatsAppClick}
                    className="whatsapp-link"
                    title="Contactar por WhatsApp"
                  >
                    {currentTexts.whatsappLink}
                  </button>
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StudentLogin;
